/*
 * CARVERHAUS surface selector (blocks/ch-surface.liquid).
 *
 * Owns one hidden input, properties[Surface], that names the buy-buttons form.
 * Choosing a colour re-renders the block from the server, so the chosen surface
 * is kept per product in sessionStorage and put back whenever the element
 * connects again.
 */
if (!customElements.get('ch-surface')) {
  customElements.define(
    'ch-surface',
    class ChSurface extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('[data-ch-surface-input]');
        this.cards = [...this.querySelectorAll('[data-ch-surface-value]')];
        this.dialog = this.querySelector('[data-ch-surface-dialog]');

        const saved = this.#read();
        const known = (value) => this.cards.some((card) => card.dataset.chSurfaceValue === value);
        this.select(saved && known(saved) ? saved : this.cards[0]?.dataset.chSurfaceValue, false);

        this.addEventListener('click', this.#onClick);
        this.addEventListener('keydown', this.#onKeydown);
        this.dialog?.addEventListener('click', this.#onBackdrop);
        document.addEventListener('change', this.#onVariantChange);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.#onClick);
        this.removeEventListener('keydown', this.#onKeydown);
        this.dialog?.removeEventListener('click', this.#onBackdrop);
        document.removeEventListener('change', this.#onVariantChange);
      }

      /* Selected colour name, read from the colour fieldset of this product's variant picker */
      get #colour() {
        const picker = this.closest('.product-details')?.querySelector('variant-picker');
        for (const fieldset of picker?.querySelectorAll('fieldset') ?? []) {
          const legend = fieldset.querySelector('legend')?.textContent?.trim().toLowerCase() ?? '';
          if (/^(colou?r|finish|wood)/.test(legend)) {
            const checked = /** @type {HTMLInputElement | null} */ (fieldset.querySelector('input:checked'));
            return checked?.value?.trim().toLowerCase() ?? null;
          }
        }
        return null;
      }

      /** @param {HTMLElement} card */
      #imagesFor(card) {
        try {
          return JSON.parse(card.dataset.chSurfaceImages || '{}');
        } catch {
          return {};
        }
      }

      /* Show each card in the newly chosen colour */
      #onVariantChange = (event) => {
        const target = /** @type {HTMLElement} */ (event.target);
        if (!target.closest?.('variant-picker')) return;
        if (target.closest('.product-details') !== this.closest('.product-details')) return;
        const colour = this.#colour;
        if (!colour) return;
        for (const card of this.cards) {
          const entry = this.#imagesFor(card)[colour];
          const img = /** @type {HTMLImageElement | null} */ (card.querySelector('img'));
          if (!entry || !img) continue;
          img.removeAttribute('srcset');
          img.src = entry[0];
        }
      };

      get #key() {
        return `ch:surface:${this.dataset.productId}`;
      }

      #read() {
        try {
          return sessionStorage.getItem(this.#key);
        } catch {
          return null;
        }
      }

      #write(value) {
        try {
          sessionStorage.setItem(this.#key, value);
        } catch {
          /* storage blocked: the choice still holds until the next re-render */
        }
      }

      select(value, remember = true) {
        if (!value || !this.input) return;
        this.input.value = value;
        for (const card of this.cards) {
          card.setAttribute('aria-checked', String(card.dataset.chSurfaceValue === value));
        }
        const current = this.querySelector('[data-ch-surface-current]');
        if (current) current.textContent = value;
        if (remember) this.#write(value);
      }

      #onClick = (event) => {
        const target = /** @type {HTMLElement} */ (event.target);

        const card = target.closest('[data-ch-surface-value]');
        if (card && this.contains(card)) {
          this.select(card.dataset.chSurfaceValue);
          return;
        }

        if (target.closest('[data-ch-surface-open]')) {
          this.#openGuide();
          return;
        }

        const choose = target.closest('[data-ch-surface-choose]');
        if (choose) {
          this.select(choose.dataset.chSurfaceChoose);
          this.dialog?.close();
          return;
        }

        if (target.closest('[data-ch-surface-close]')) this.dialog?.close();
      };

      /* Arrow keys move between the two cards, as in a radio group */
      #onKeydown = (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
        const index = this.cards.indexOf(/** @type {HTMLElement} */ (document.activeElement));
        if (index < 0) return;
        event.preventDefault();
        const step = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1;
        const next = this.cards[(index + step + this.cards.length) % this.cards.length];
        next.focus();
        this.select(next.dataset.chSurfaceValue);
      };

      /* The compare dialog is built from each option's <template> when opened */
      #openGuide() {
        if (!this.dialog) return;
        const compare = this.dialog.querySelector('[data-ch-surface-compare]');
        if (compare) {
          const colour = this.#colour;
          compare.replaceChildren(
            ...this.cards.map((card) => {
              const template = /** @type {HTMLTemplateElement | null} */ (
                card.nextElementSibling?.matches('template[data-ch-surface-detail]') ? card.nextElementSibling : null
              );
              const detail = template ? template.content.cloneNode(true) : document.createDocumentFragment();
              const entry = colour ? this.#imagesFor(card)[colour] : null;
              const img = /** @type {HTMLImageElement | null} */ (detail.querySelector?.('img'));
              if (entry && img) {
                img.removeAttribute('srcset');
                img.src = entry[1];
              }
              return detail;
            })
          );
        }
        this.dialog.showModal();
      }

      #onBackdrop = (event) => {
        if (event.target === this.dialog) this.dialog.close();
      };
    }
  );
}
